<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useDocumentVisibility, useOnline } from '@vueuse/core'

const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || 'dev'
const IS_PROD = import.meta.env.PROD

const STORAGE_KEY = 'app_reloaded_for_version'
const VERSION_URL = '/version.json'
const INITIAL_DELAY_MS = 2_000
const POLL_INTERVAL_MS = 60_000

let intervalId: ReturnType<typeof setInterval> | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null
let controller: AbortController | null = null
let isChecking = false

const visibility = useDocumentVisibility()
const isOnline = useOnline()

const safeGetStoredVersion = (): string | null => {
    try {
        return localStorage.getItem(STORAGE_KEY)
    } catch {
        return null
    }
}

const safeSetStoredVersion = (version: string) => {
    try {
        localStorage.setItem(STORAGE_KEY, version)
    } catch {
        /* localStorage may be unavailable (private mode / quota) */
    }
}

const clearTimers = () => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
    }
    if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
    }
}

const reloadApp = (newVersion: string) => {
    safeSetStoredVersion(newVersion)
    clearTimers()
    controller?.abort()
    controller = null
    location.reload()
}

const checkVersion = async () => {
    if (isChecking) return
    // Skip when tab is hidden or offline to avoid wasted requests and noisy logs.
    if (visibility.value !== 'visible') return
    if (!isOnline.value) return

    isChecking = true
    controller = new AbortController()

    try {
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
            cache: 'no-store',
            signal: controller.signal,
        })

        if (!res.ok) throw new Error(`Version check failed: ${res.status}`)

        const { version } = (await res.json()) as { version?: string }

        if (!version || version === CURRENT_VERSION) return

        // Guards against a reload loop when the served `version.json` and the
        // bundled VITE_APP_VERSION never converge (e.g. build pipeline mismatch).
        if (safeGetStoredVersion() === version) return

        if (IS_PROD) reloadApp(version)
    } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('[VersionCheck] failed:', err)
    } finally {
        isChecking = false
    }
}

const startPolling = () => {
    clearTimers()
    timeoutId = setTimeout(() => {
        timeoutId = null
        checkVersion()
        intervalId = setInterval(checkVersion, POLL_INTERVAL_MS)
    }, INITIAL_DELAY_MS)
}

onMounted(() => {
    if (!IS_PROD) return
    startPolling()
})

// Re-check as soon as the user returns to the tab so they don't keep using a
// stale build while polling was effectively paused.
watch(visibility, (state, prev) => {
    if (!IS_PROD) return
    if (state === 'visible' && prev !== 'visible') checkVersion()
})

onUnmounted(() => {
    controller?.abort()
    controller = null
    clearTimers()
})
</script>

<template>
  <!-- Headless: polls /version.json; no visible UI -->
  <span
    class="sr-only"
    aria-hidden="true"
  />
</template>
